import axios, { AxiosInstance, AxiosError } from 'axios';
import {
  User,
  Project,
  Template,
  Checklist,
  ChecklistItem,
  Lookups,
  AuthResponse,
  GateSummary,
  GateSummaryResponse,
  ChecklistProgress,
  ChecklistItemFilters,
  ProjectTeamAssignment,
  UpsertTeamAssignmentRequest,
  Attachment,
  ActionQueueType,
  AssignablePerson,
} from '../types';

const API_URL = process.env.REACT_APP_API_URL || '/api';

class ApiService {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_URL,
      headers: {
        'Content-Type': 'application/json',
      },
      withCredentials: true,  // Send cookies/auth headers with cross-origin requests
    });

    // Add request interceptor to include auth token
    this.client.interceptors.request.use((config) => {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    // Add response interceptor to handle auth errors
    this.client.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        if (error.response?.status === 401) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  // Auth
  async login(email: string, password: string): Promise<AuthResponse> {
    const response = await this.client.post<AuthResponse>('/auth/login', { email, password });
    return response.data;
  }

  async getCurrentUser(): Promise<User> {
    const response = await this.client.get<User>('/auth/me');
    return response.data;
  }

  // Lookups
  async getLookups(): Promise<Lookups> {
    // Fetch all lookups in parallel for better reliability
    const [teams, statuses, gates, priorities, itemTypes, proofTypes, modules] = await Promise.all([
      this.client.get('/lookups/teams'),
      this.client.get('/lookups/statuses'),
      this.client.get('/lookups/gates'),
      this.client.get('/lookups/priorities'),
      this.client.get('/lookups/item-types'),
      this.client.get('/lookups/proof-types'),
      this.client.get('/lookups/modules'),
    ]);
    return {
      teams: teams.data,
      statuses: statuses.data,
      gates: gates.data,
      priorities: priorities.data,
      itemTypes: itemTypes.data,
      proofTypes: proofTypes.data,
      modules: modules.data,
    };
  }

  // Projects
  async getProjects(status?: string): Promise<Project[]> {
    const params = status ? { status } : {};
    const response = await this.client.get<Project[]>('/projects', { params });
    return response.data;
  }

  async getProject(id: string): Promise<Project> {
    const response = await this.client.get<Project>(`/projects/${id}`);
    return response.data;
  }

  async createProject(data: Partial<Project>): Promise<Project> {
    const response = await this.client.post<Project>('/projects', data);
    return response.data;
  }

  async updateProject(id: string, data: Partial<Project>): Promise<Project> {
    const response = await this.client.put<Project>(`/projects/${id}`, data);
    return response.data;
  }

  async deleteProject(id: string): Promise<void> {
    await this.client.delete(`/projects/${id}`);
  }

  // Project Team Assignments
  async getProjectTeamAssignments(projectId: string): Promise<ProjectTeamAssignment[]> {
    const response = await this.client.get<ProjectTeamAssignment[]>(`/projects/${projectId}/team-assignments`);
    return response.data;
  }

  async upsertProjectTeamAssignment(projectId: string, data: UpsertTeamAssignmentRequest): Promise<ProjectTeamAssignment> {
    const response = await this.client.post<ProjectTeamAssignment>(`/projects/${projectId}/team-assignments`, data);
    return response.data;
  }

  async bulkUpsertProjectTeamAssignments(projectId: string, assignments: UpsertTeamAssignmentRequest[]): Promise<ProjectTeamAssignment[]> {
    const response = await this.client.put<ProjectTeamAssignment[]>(`/projects/${projectId}/team-assignments`, { assignments });
    return response.data;
  }

  async removeProjectTeamAssignment(projectId: string, assignmentId: string): Promise<void> {
    await this.client.delete(`/projects/${projectId}/team-assignments/${assignmentId}`);
  }

  async getAssignablePeople(projectId: string, teamCodes?: string[]): Promise<AssignablePerson[]> {
    const params = teamCodes?.length ? { teams: teamCodes.join(',') } : {};
    const response = await this.client.get<AssignablePerson[]>(`/projects/${projectId}/assignable-people`, { params });
    return response.data;
  }

  // Templates
  async getTemplates(): Promise<Template[]> {
    const response = await this.client.get<Template[]>('/templates');
    return response.data;
  }

  async getTemplate(id: string): Promise<Template> {
    const response = await this.client.get<Template>(`/templates/${id}`);
    return response.data;
  }

  // Checklists
  async getChecklists(projectId?: string): Promise<Checklist[]> {
    const params = projectId ? { projectId } : {};
    const response = await this.client.get<Checklist[]>('/checklists', { params });
    return response.data;
  }

  async getChecklist(id: string): Promise<Checklist> {
    const response = await this.client.get<Checklist>(`/checklists/${id}`);
    return response.data;
  }

  async createChecklist(data: { projectId: string; templateId: string; name: string }): Promise<Checklist> {
    const response = await this.client.post<Checklist>('/checklists', data);
    return response.data;
  }

  async updateChecklist(id: string, data: Partial<Checklist>): Promise<Checklist> {
    const response = await this.client.put<Checklist>(`/checklists/${id}`, data);
    return response.data;
  }

  async deleteChecklist(id: string): Promise<void> {
    await this.client.delete(`/checklists/${id}`);
  }

  async getChecklistProgress(id: string): Promise<ChecklistProgress> {
    const response = await this.client.get<ChecklistProgress>(`/checklists/${id}/progress`);
    return response.data;
  }

  async getChecklistGateItems(checklistId: string, gateNumber: number): Promise<ChecklistItem[]> {
    const response = await this.client.get<ChecklistItem[]>(`/checklists/${checklistId}/gate/${gateNumber}`);
    return response.data;
  }

  // Checklist Items
  async getChecklistItems(checklistId: string, filters?: ChecklistItemFilters): Promise<ChecklistItem[]> {
    const response = await this.client.get<ChecklistItem[]>(`/checklist-items/checklist/${checklistId}`, {
      params: filters,
    });
    return response.data;
  }

  async getChecklistItem(id: string): Promise<ChecklistItem> {
    const response = await this.client.get<ChecklistItem>(`/checklist-items/${id}`);
    return response.data;
  }

  async updateChecklistItem(id: string, data: Partial<ChecklistItem>): Promise<ChecklistItem> {
    const response = await this.client.put<ChecklistItem>(`/checklist-items/${id}`, data);
    return response.data;
  }

  async updateItemStatus(id: string, status: string, assignedReceiverEmail?: string): Promise<ChecklistItem> {
    const body: { status: string; assignedReceiverEmail?: string } = { status };
    if (assignedReceiverEmail) {
      body.assignedReceiverEmail = assignedReceiverEmail;
    }
    const response = await this.client.put<ChecklistItem>(`/checklist-items/${id}/status`, body);
    return response.data;
  }

  async markItemNA(id: string, reason: string): Promise<ChecklistItem> {
    const response = await this.client.post<ChecklistItem>(`/checklist-items/${id}/mark-na`, { reason });
    return response.data;
  }

  async getGateSummary(checklistId: string): Promise<GateSummary[]> {
    const response = await this.client.get<GateSummaryResponse>(`/checklist-items/checklist/${checklistId}/gate-summary`);
    // Transform the object response to array format
    const gateNames: Record<string, string> = {
      '0': 'Gate 0: Thesis + Control',
      '1': 'Gate 1: Site Recon',
      '2': 'Gate 2: Title/Survey',
      '3': 'Gate 3: Technical DD',
      '4': 'Gate 4: Entitlements',
      '5': 'Gate 5: Capital + Closing',
      '6': 'Gate 6: Build + Closeout',
    };
    return Object.entries(response.data).map(([gateNum, data]) => ({
      gateNumber: parseInt(gateNum),
      gateName: gateNames[gateNum] || `Gate ${gateNum}`,
      total: data.total,
      completed: data.completed,
      inProgress: data.inProgress,
      notStarted: data.notStarted,
      naCount: data.na,
      completionPercentage: data.percentComplete,
    })).sort((a, b) => a.gateNumber - b.gateNumber);
  }

  async bulkUpdateItems(itemIds: string[], updates: Partial<ChecklistItem>): Promise<ChecklistItem[]> {
    const response = await this.client.post<ChecklistItem[]>('/checklist-items/bulk-update', {
      itemIds,
      updates,
    });
    return response.data;
  }

  // Users
  async getUsers(): Promise<User[]> {
    const response = await this.client.get<User[]>('/users');
    return response.data;
  }

  async getUser(id: string): Promise<User> {
    const response = await this.client.get<User>(`/users/${id}`);
    return response.data;
  }

  async createUser(data: { email: string; name: string }): Promise<User> {
    const response = await this.client.post<User>('/users', data);
    return response.data;
  }

  async getTeams(): Promise<{ code: string; name: string }[]> {
    const response = await this.client.get('/lookups/teams');
    return response.data;
  }

  // Action Queue
  async getActionQueueItems(queueType: ActionQueueType): Promise<ChecklistItem[]> {
    const response = await this.client.get<ChecklistItem[]>(`/checklist-items/action-queue/${queueType}`);
    return response.data;
  }

  // Attachments
  async uploadAttachment(checklistItemId: string, file: File): Promise<Attachment> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await this.client.post<Attachment>(
      `/attachments/checklist-item/${checklistItemId}`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  }

  async getAttachments(checklistItemId: string): Promise<Attachment[]> {
    const response = await this.client.get<Attachment[]>(`/attachments/checklist-item/${checklistItemId}`);
    return response.data;
  }

  async deleteAttachment(id: string): Promise<void> {
    await this.client.delete(`/attachments/${id}`);
  }

  getAttachmentDownloadUrl(id: string): string {
    return `${API_URL}/attachments/${id}/download`;
  }

  // Resolve item with response
  async resolveItem(id: string, data: { response?: string }): Promise<ChecklistItem> {
    const response = await this.client.put<ChecklistItem>(`/checklist-items/${id}`, {
      status: 'resolved',
      ...data,
    });
    return response.data;
  }
}

export const api = new ApiService();
export default api;
